import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Button,
  Dropdown,
  Badge,
  ProgressBar
} from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { Bars } from 'react-loader-spinner';

import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider, {
  useAdvanceTableContext
} from '../../providers/AdvanceTableProvider';
import FilterTab from '../../components/common/FilterTab';
import SearchBox from '../../components/common/SearchBox';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import { useAppContext } from '../../providers/AppProvider';
import { useOpenLines } from '../../features/orders/useOrders';
import { buildDeliverySummary } from '../../helpers/deliverySummary';
import QueueTable from './Project Simulation/QueueTable';
import { http } from '../../features/jobs/api/http';
import MaterialTotalsChart from '../../components/charts/materials-charts/MaterialTotalsChart';
import MaterialTotalsArrayChart from '../../components/charts/materials-charts/MaterialTotalsArrayChart';
import ColorLabel from '../../components/common/ColorLabel';
import { cableSearchKey, matchesSearch } from '../../helpers/cableSearch';
import ProductAllocationTable from '../../components/project-management/ProductAllocationTable';
import PhoenixBadge from '../../components/base/Badge';
import { formatCoreSpec } from '../../helpers/productLabel';

// =====================
// small utils
// =====================
const U = v =>
  String(v ?? '')
    .trim()
    .toUpperCase();
const fmt = n => Number(n || 0).toLocaleString();

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// ✅ key stabil pt map/queue/dedupe
const keyOf = r => `${r?.orderId ?? ''}::${r?.lineId ?? ''}`;

// =====================
// Top Section
// =====================
export const OpenLinesTopSection = ({
  selectedCount = 0,
  onAddSelected,
  actionLabel = 'Add',
  actionIcon = faPlus
}) => {
  const { setGlobalFilter, getPrePaginationRowModel, getColumn } =
    useAdvanceTableContext();

  const handleFilterItemClick = (columnId, value) => {
    const column = getColumn(columnId);
    column?.setFilterValue(value === 'all' ? '' : value);
  };

  const tabItems = useMemo(() => {
    const rows = getPrePaginationRowModel().rows;

    const counts = new Map();
    for (const r of rows) {
      const name = U(r?.original?.cableName || r?.original?.product?.cableName);
      if (!name) continue;
      counts.set(name, (counts.get(name) || 0) + 1);
    }

    const names = Array.from(counts.keys()).sort((a, b) => a.localeCompare(b));
    const MAX_TABS = 10;
    const visible = names.slice(0, MAX_TABS);

    const base = [
      {
        label: 'All',
        value: 'all',
        onClick: () => handleFilterItemClick('cableName', 'all'),
        count: rows.length
      }
    ];

    const dynamic = visible.map(name => ({
      label: name,
      value: name,
      onClick: () => handleFilterItemClick('cableName', name),
      count: counts.get(name) || 0
    }));

    return [...base, ...dynamic];
  }, [getPrePaginationRowModel, getColumn]);

  const handleSearchInputChange = e => {
    setGlobalFilter(e.target.value || undefined);
  };

  return (
    <Row className="g-3 justify-content-between align-items-center mb-3">
      <Col xs={12} sm="auto">
        <FilterTab tabItems={tabItems} />
      </Col>

      <Col xs={12} sm="auto">
        <div className="d-flex align-items-center gap-1">
          <SearchBox
            onChange={handleSearchInputChange}
            placeholder="Search open lines (client, product, section...)"
            style={{ maxWidth: '30rem' }}
            className="me-3"
          />

          {selectedCount > 0 && (
            <Button variant="primary" onClick={onAddSelected}>
              <FontAwesomeIcon icon={actionIcon} className="me-2" />
              {actionLabel} ({selectedCount})
            </Button>
          )}
        </div>
      </Col>
    </Row>
  );
};

// =====================
// Plan badge helpers
// =====================
const planBadge = status => {
  if (status === 'planned') return { label: 'Planned', color: 'success' };
  if (status === 'partial') return { label: 'Partial', color: 'warning' };
  return { label: 'Missing', color: 'danger' };
};

// =====================
// Columns
// =====================
export const openLinesTableColumns = [
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row: { original } }) => (
      <span className="white-space-nowrap text-body-tertiary fs-9">
        {original.createdAt || <span className="text-muted">—</span>}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '9%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-3' }
    }
  },
  {
    accessorKey: 'dueDate',
    header: 'Due',
    cell: ({ row: { original } }) => (
      <span className="white-space-nowrap">
        {original.dueDate || <span className="text-muted">—</span>}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-3' }
    }
  },
  {
    accessorKey: 'client',
    header: 'Client',
    cell: ({ row: { original } }) =>
      original.client ? (
        <PhoenixBadge variant="phoenix" bg="primary" className="fs-10">
          {U(original.client)}
        </PhoenixBadge>
      ) : (
        <span className="text-muted">—</span>
      ),
    meta: {
      headerProps: { style: { width: '12%' } },
      cellProps: { className: 'white-space-nowrap py-3' }
    }
  },
  // ✅ column id for FilterTab: getColumn('cableName')
  {
    id: 'cableName',
    header: 'Cable',
    accessorFn: row => row.cableName,
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column gap-1">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semi-bold">{original.cableName || '—'}</span>
          <ColorLabel colorName={original.color} />
        </div>
        <span className="text-muted fs-10">
          {original.orderCode} •{' '}
          <Link to={`/apps/sales-management/order-details/${original.orderId}`}>
            open order
          </Link>
        </span>
      </div>
    ),
    meta: {
      headerProps: { style: { minWidth: 240 } },
      cellProps: { className: 'py-3' }
    }
  },
  {
    accessorKey: 'spec',
    header: 'Spec',
    cell: ({ row: { original } }) => (
      <span className="fw-semi-bold white-space-nowrap">
        {original.spec || '—'}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '12%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-3' }
    }
  },
  {
    accessorKey: 'totalOrderedLength',
    header: 'Total (m)',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column align-items-end">
        <span className="fw-semi-bold">{fmt(original.totalOrderedLength)}</span>
        {Number(original.stockReservedLength) > 0 && (
          <span className="text-success fs-10">
            {fmt(original.stockReservedLength)} m from stock
          </span>
        )}
      </div>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-3' }
    }
  },
  {
    accessorKey: 'remainingToPlan',
    header: 'Remaining (m)',
    cell: ({ row: { original } }) => (
      <span className="fw-bold">{fmt(original.remainingToPlan)}</span>
    ),
    meta: {
      headerProps: { style: { width: '12%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-3' }
    }
  },
  {
    id: 'planStatus',
    header: 'Plan',
    accessorFn: row => row.planStatus,
    cell: ({ row: { original } }) => {
      const b = planBadge(original.planStatus);

      return (
        <div className="d-flex flex-column align-items-center gap-1">
          <div className="d-flex align-items-center gap-2">
            <Badge bg={b.color} className="fs-10 rounded-pill px-3 py-2">
              {b.label}
            </Badge>

            {original.plannedSource === 'simulation' && (
              <Badge bg="primary" className="fs-10 rounded-pill px-2 py-1">
                SIM
              </Badge>
            )}
          </div>

          <div style={{ width: 140 }}>
            <ProgressBar
              now={original.planProgress}
              style={{ height: 6 }}
              variant={
                b.color === 'danger'
                  ? 'danger'
                  : b.color === 'warning'
                    ? 'warning'
                    : 'success'
              }
            />
          </div>

          <span className="text-muted fs-10">
            {fmt(original.plannedTotal)} / {fmt(original.totalOrderedLength)} m
          </span>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '18%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-3' }
    }
  },
  {
    id: 'summary',
    header: 'Summary',
    cell: ({ row: { original } }) => (
      <div style={{ maxWidth: 360 }}>
        <div className="text-truncate" title={original.deliverySummary || ''}>
          {original.deliverySummary || <span className="text-muted">—</span>}
        </div>
      </div>
    ),
    meta: {
      headerProps: { style: { width: '20%' } },
      cellProps: { className: 'py-3' }
    }
  },
  {
    // SOFT "prepared in Future Products" marker — distinctive, shown in BOTH the
    // Project-Simulation and Future-Products open-lines tables. Does not reduce
    // the plannable remaining (it's a sandbox reservation).
    id: 'futureReserved',
    header: 'In future',
    accessorFn: row => Number(row.futureReservedMeters) || 0,
    cell: ({ row: { original } }) => {
      const m = Number(original.futureReservedMeters) || 0;
      if (m <= 0) return <span className="text-muted">—</span>;
      const total = Number(original.totalOrderedLength) || 0;
      const alloc = Number(original.allocatedLength) || 0;
      const needPlan = Math.max(0, total - alloc);
      const full = needPlan > 0 && m >= needPlan - 0.5;
      return (
        <span
          className="badge fs-10 rounded-pill px-3 py-2"
          style={{ background: full ? '#7048e8' : '#e64980', color: '#fff' }}
          title={`Prepared in Future Products: ${m.toLocaleString()} m`}
        >
          {full ? 'IN FUTURE · FULL' : 'IN FUTURE · PARTIAL'} —{' '}
          {m.toLocaleString()} m
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '13%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-3' }
    }
  },
  {
    id: 'action',
    header: '',
    cell: () => (
      <Dropdown>
        <Dropdown.Toggle
          variant="phoenix-secondary"
          size="sm"
          className="bg-white hover-bg-100 dropdown-caret-none"
        >
          <FontAwesomeIcon icon={faEllipsisH} className="10" />
        </Dropdown.Toggle>

        {/* `strategy: 'fixed'` floats the menu out of the table's scroll box.
            Without it the last rows' menu opens inside a clipped container and
            you have to scroll the table to reach items that are already open —
            the same fix WarehouseTransferTable and the order-products table
            already carry. `renderOnMount` so Popper measures it before the
            first open rather than after. */}
        <Dropdown.Menu align="end" renderOnMount popperConfig={{ strategy: 'fixed' }}>
          <Dropdown.Item>Simulate</Dropdown.Item>
          <Dropdown.Item>Launch</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    ),
    meta: {
      headerProps: { style: { width: '4%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-3' }
    }
  }
];

// =====================
// Open Lines Table wrapper
// =====================
export const OpenLinesTable = () => {
  return (
    <div className="border-bottom">
      <AdvanceTable
        tableProps={{
          className: 'phoenix-table border-top border-200 fs-9 table-fixed'
        }}
      />
      <AdvanceTableFooter pagination className="py-3" />
    </div>
  );
};

// =====================
// adapter: line -> table row (with queue override)
// =====================
export const adaptOpenLineRow = (line, queuedLengthByKey) => {
  const p = line?.product || {};
  const cableName = U(p.cableName);
  const color = U(p.color);

  const numberOfConductors = Number(p.numberOfConductors) || 0;
  const section = Number(p.section) || 0;

  const hasMixed = Boolean(p.hasMixedConductorSections);
  const gy = hasMixed ? Number(p.greenYellowSection) || null : null;

  const spec = formatCoreSpec({ numberOfConductors, section, greenYellowSection: gy });

  const totalOrderedLength = toNum(line?.totalOrderedLength);
  const remainingToPlan = toNum(line?.remainingToPlan);

  // ✅ baza “planului” vine din TOTAL - REMAINING
  const plannedBase = clamp(
    totalOrderedLength - remainingToPlan,
    0,
    totalOrderedLength
  );

  // ✅ simularea (launchLength) se ADUNĂ peste baza (nu o înlocuiește)
  const queuedLaunch = toNum(queuedLengthByKey.get(keyOf(line)) ?? 0);
  const plannedTotal = clamp(plannedBase + queuedLaunch, 0, totalOrderedLength);
  const remainingSim = clamp(
    remainingToPlan - queuedLaunch,
    0,
    remainingToPlan
  );

  const planProgress =
    totalOrderedLength > 0
      ? Math.min(100, Math.round((plannedTotal / totalOrderedLength) * 100))
      : 0;

  const planStatus =
    plannedTotal <= 0
      ? 'missing'
      : plannedTotal < totalOrderedLength
        ? 'partial'
        : 'planned';

  return {
    ...line,
    cableName,
    spec,
    color,

    plannedBase, // (opțional pt debug)
    queuedLaunch, // (opțional pt debug)
    remainingToPlan: remainingSim,
    plannedTotal,
    planProgress,
    planStatus,
    plannedSource: queuedLaunch > 0 ? 'simulation' : 'backend',

    // rebuild from items[] so TAMBURI (drums) aren't dropped (stored summary was
    // coils-only for the new delivery-plan format).
    deliverySummary: buildDeliverySummary(line?.deliveryPlan)
  };
};

// =====================
// MAIN
// =====================
const OrderSimulation = () => {
  const {
    config: { theme }
  } = useAppContext();

  const [queue, setQueue] = useState([]);
  const { data: lines = [], isLoading } = useOpenLines();
  const [simulationResults, setSimulationResults] = useState(null);

  // Map: key -> launchLength (from queue)
  const queuedLengthByKey = useMemo(() => {
    const m = new Map();
    for (const q of queue) {
      m.set(keyOf(q), toNum(q.launchLength));
    }
    return m;
  }, [queue]);

  // Table rows with override
  const tableData = useMemo(
    () => lines.map(l => adaptOpenLineRow(l, queuedLengthByKey)),
    [lines, queuedLengthByKey]
  );

  const table = useAdvanceTable({
    data: tableData,
    columns: openLinesTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true,
    // The stored spec is "3×2.5 + GY 16"; nobody types that. Match against a
    // blob that carries every spelling the line could be called by, so the
    // obvious search finds the obvious thing.
    globalFilterFn: (row, _columnId, value) =>
      matchesSearch(cableSearchKey(row.original), value)
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedOriginals = selectedRows.map(r => r.original);
  const selectedCount = selectedOriginals.length;

  /**
   * Ticking rows above narrows the products below to the ones that could serve
   * those lines. Nothing ticked means everything, which is the useful default:
   * a planner opening the page wants to see what is on the floor, not an empty
   * panel asking them to choose first.
   */
  const globalFilter = table.getState().globalFilter;
  const filteredRows = table.getFilteredRowModel().rows;

  const lineFilter = useMemo(() => {
    // Ticked rows win — an explicit choice beats a search.
    const rows = selectedOriginals.length
      ? selectedOriginals
      : // Otherwise the search narrows it: typing a customer's name leaves
        // their lines, and the products below should be the ones that could
        // serve THOSE lines rather than everything on the floor.
        globalFilter
        ? filteredRows.map(r => r.original)
        : null;

    if (!rows) return null;
    return new Set(
      rows.filter(r => r?.orderId && r?.lineId).map(r => `${r.orderId}::${r.lineId}`)
    );
  }, [selectedOriginals, filteredRows, globalFilter]);

  // Track the ORDER in which rows were selected (click order), because
  // getSelectedRowModel() returns rows in table/display order — and the launch
  // order must follow the user's selection order.
  const rowSelection = table.getState().rowSelection;
  const [selOrder, setSelOrder] = useState([]);
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection || {}).filter(
      id => rowSelection[id]
    );
    setSelOrder(prev => {
      const kept = prev.filter(id => selectedIds.includes(id));
      const added = selectedIds.filter(id => !kept.includes(id));
      const nextArr = [...kept, ...added];
      // avoid re-render churn when nothing actually changed
      if (
        nextArr.length === prev.length &&
        nextArr.every((v, i) => v === prev[i])
      ) {
        return prev;
      }
      return nextArr;
    });
  }, [rowSelection]);

  const handleAddSelected = useCallback(() => {
    if (!selectedOriginals.length) return;

    // reorder selected rows by click order (fallback to table order)
    const orderedOriginals = selOrder
      .map(id => selectedRows.find(r => String(r.id) === String(id))?.original)
      .filter(Boolean);
    const finalList =
      orderedOriginals.length === selectedOriginals.length
        ? orderedOriginals
        : selectedOriginals;

    setQueue(prev => {
      const seen = new Set(prev.map(keyOf));
      const next = [...prev];

      for (const r of finalList) {
        const k = keyOf(r);
        if (seen.has(k)) continue;
        seen.add(k);

        next.push({
          ...r, // ✅ keep whole row
          launchLength: toNum(r.remainingToPlan) // ✅ default
        });
      }
      return next;
    });

    table.resetRowSelection();
    setSelOrder([]);
  }, [selectedOriginals, selectedRows, selOrder, table]);

  const updateQueueLength = useCallback((k, value) => {
    setQueue(prev =>
      prev.map(item => {
        if (keyOf(item) !== k) return item;

        const max =
          toNum(item.remainingToPlan) || toNum(item.totalOrderedLength) || 0;
        const nextLen = clamp(toNum(value), 0, max);

        return { ...item, launchLength: nextLen };
      })
    );
  }, []);

  const removeFromQueue = useCallback(k => {
    setQueue(prev => prev.filter(item => keyOf(item) !== k));
  }, []);

  // Reorder the launch queue — the order here IS the order products enter the
  // wizard and the machine queue (row 1 launches first).
  const moveQueueItem = useCallback((index, dir) => {
    setQueue(prev => {
      const to = index + dir;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  // carried from the Future-products page: prepare an isolated future product
  // instead of launching a real job (the create wizard reads this flag too).
  const isFuture = !!location.state?.future;

  function launchGroup(queue) {
    const items = (queue || []).map(l => ({
      kind: 'ORDER_LINE',
      orderId: l.orderId,
      lineId: l.lineId,
      orderCode: l.orderCode,
      client: l.client,
      productKey: l.productKey,
      product: l.product,
      launchLength: Number(l.launchLength || 0),
      deliveryPlan: l.deliveryPlan,
      remainingToPlan: l.remainingToPlan,
      totalOrderedLength: l.totalOrderedLength,
      dueDate: l.dueDate
    }));
    console.log('Launching with items: ', items);
    navigate('/apps/project-management/create-launch', {
      state: {
        launchRequest: {
          launchKind: 'ORDERS',
          items
        },
        future: isFuture
      }
    });
  }

  async function simulateQueue(queue) {
    const items = queue.map(q => ({
      cableName: q.product?.cableName ?? q.cableName,
      numberOfConductors: q.product?.numberOfConductors ?? q.numberOfConductors,
      section: q.product?.section ?? q.section,
      color: q.product?.color ?? q.color,
      hasMixedConductorSections:
        q.product?.hasMixedConductorSections ?? q.hasMixedConductorSections,
      greenYellowSection: q.product?.greenYellowSection ?? q.greenYellowSection,

      // 🔥 lungimea simulată
      length: Number(q.launchLength || 0)
    }));

    const res = await http.post('/api/redis/orders/simulate-materials', {
      items
    });
    setSimulationResults(() => ({
      copperByDiameter: res.data.copperByDiameter,
      pvcByGrade: res.data.pvcByGrade
    }));

    return res.data;
  }

  if (isLoading) {
    return (
      <Bars
        height="80"
        width="80"
        radius="9"
        color={theme === 'dark' ? 'white' : '#3874ff'}
        ariaLabel="loading"
        wrapperStyle={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
      />
    );
  }

  return (
    <div className="mb-9">
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
          <h2 className="mb-0">
            <span className="me-3">Open Lines</span>{' '}
            <span className="fw-normal text-body-tertiary">
              ({tableData.length})
            </span>
          </h2>
        </div>

        <OpenLinesTopSection
          selectedCount={selectedCount}
          onAddSelected={handleAddSelected}
        />

        <OpenLinesTable />
      </AdvanceTableProvider>

      {/*
        Under the lines, the same question asked in the unit the factory works
        in. Sales reserves metres; this reserves drums, and a drum either has a
        continuous 15,000 m on it or it does not. Driven by the row selection so
        a planner picks the line and reads the answer for it — with nothing
        selected there is nothing to be feasible about.
      */}
      {/* Product-centric: the planner's axis. The cable is the scarce thing and
          the decision is who gets it, so the product is the row and the orders
          are the choice inside it. */}
      <ProductAllocationTable lineFilter={lineFilter} search={globalFilter || ''} />

      {/* Queue below (outside provider e ok) */}

      <QueueTable
        queue={queue}
        onChangeLength={updateQueueLength}
        onRemove={removeFromQueue}
        onReorder={moveQueueItem}
        onClear={() => {
          setQueue([]);
          setSimulationResults(null);
        }}
        onSimulate={simulateQueue}
        onLaunch={launchGroup}
      />
      {simulationResults && (
        <div className="mt-4">
          <h4>Total Material Consumption</h4>
          <div className="row">
            <div className="col-md-4 mb-3">
              <MaterialTotalsArrayChart
                data={simulationResults.copperByDiameter}
                title="Copper Consumption"
                unit="kg"
                labelKey="wireDiameter"
                valueKey="planned_kg"
                labelFormatter={d => `Ø ${Number(d).toFixed(3)}`}
                valueFormatter={v => Number(v || 0)}
                barWidth={12}
                rotateLabels={0}
              />
            </div>

            <div className="col-md-8 mb-3">
              <MaterialTotalsArrayChart
                data={simulationResults.pvcByGrade}
                title="Material Consumption (PVC, PE, etc)"
                unit="kg"
                labelKey="grade"
                valueKey="planned_kg"
                labelFormatter={g => String(g || '—')}
                valueFormatter={v => Number(v || 0)}
                barWidth={18}
                rotateLabels={0}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSimulation;
